<template>
  <CCard>
    <CCardHeader v-if="showHeader" class="border-0 px-4 pb-1 pt-4">
      <div class="d-flex align-items-center font-weight-medium">
        <CIcon name="cipUser" size="custom-size" :width="18" class="mr-2"/>
        {{ userDisplayName }}
      </div>
      <div class="card-header-actions">
        <CButton
          color="primary"
          variant="outline"
          size="sm"
          class="ml-3 text-nowrap"
          :to="{ name: 'customer_success.user', params: { id: userId }}"
        >
          <translate translate-context="accounting.user">View detail</translate>
          <CIcon name="cipExternalLink" size="sm" class="ml-1"/>
        </CButton>
      </div>
    </CCardHeader>
    <CCardBody>
      <div class="user-info" v-if="user || userId">
        <!-- Two columns: Transactions and Checkins -->
        <CRow>
          <!-- Transactions Column -->
          <CCol md="6">
            <div class="mb-2">
              <div class="font-weight-medium">
                <CIcon name="cipCreditCard02" size="custom-size" :height="18" class="mr-2"/>
                <translate translate-context="accounting.user">Recent Transactions</translate>
                <CBadge v-if="totalTransactions > 0" color="primary" class="ml-2">
                  {{ totalTransactions }}
                </CBadge>
              </div>
            </div>

            <div v-if="loadingTransactions" class="text-muted small py-2">
              <CSpinner size="sm" class="mr-2"/>
              <translate translate-context="accounting.user">Loading...</translate>
            </div>

            <div v-else-if="transactions.length === 0" class="py-2 text-muted small">
              <translate translate-context="accounting.user">No transactions found</translate>
            </div>

            <div v-else class="user-items-list">
              <router-link
                v-for="tx in transactions"
                :key="tx.id"
                :to="getTransactionRoute(tx)"
                :class="['list-item', { 'list-item-current': isCurrentTransaction(tx) }]"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="mr-2" style="min-width: 0">
                    <template v-if="tx.pica_services && tx.pica_services.length">
                      <div v-for="ps in tx.pica_services" :key="ps.id">
                        <span class="font-weight-medium">{{ ps.event_name }}</span>
                        <span v-if="ps.bought_photos" class="small text-muted ml-1">&middot; {{ ps.bought_photos }} photos</span>
                      </div>
                    </template>
                    <div v-else>
                      <span class="font-weight-medium">{{ getTransactionLabel(tx) }}</span>
                    </div>
                    <div class="small text-muted" v-if="tx.refund_motivation">
                      {{ tx.refund_motivation }}
                    </div>
                  </div>
                  <div class="text-right text-nowrap">
                    <strong :class="getAmountClass(tx.total_amount)">{{ tx.total_amount|currency(tx.currency || currencyCodes.EUR) }}</strong>
                    <div class="small text-muted">{{ tx.effective_date|formatDateTime('L LT') }}</div>
                  </div>
                </div>
              </router-link>

              <CButton
                v-if="hasMoreTransactions"
                color="link"
                size="sm"
                class="p-0 mt-2"
                :disabled="loadingMoreTransactions"
                @click="loadMoreTransactions"
              >
                <CSpinner v-if="loadingMoreTransactions" size="sm" class="mr-1"/>
                <translate translate-context="accounting.user">Show more</translate>
              </CButton>
            </div>
          </CCol>

          <!-- Checkins Column -->
          <CCol md="6">
            <div class="mb-2">
              <div class="font-weight-medium">
                <CIcon name="cipQrCode02" size="custom-size" :height="18" class="mr-2"/>
                <translate translate-context="accounting.user">Recent Checkins</translate>
                <CBadge v-if="totalCheckins > 0" color="success" class="ml-2">
                  {{ totalCheckins }}
                </CBadge>
              </div>
            </div>

            <div v-if="loadingCheckins" class="text-muted small py-2">
              <CSpinner size="sm" class="mr-2"/>
              <translate translate-context="accounting.user">Loading...</translate>
            </div>

            <div v-else-if="checkins.length === 0" class="py-2 text-muted small">
              <translate translate-context="accounting.user">No checkins found</translate>
            </div>

            <div v-else class="user-items-list">
              <router-link
                v-for="checkin in checkins"
                :key="checkin.id"
                :to="getCheckinRoute(checkin)"
                :class="['list-item', { 'list-item-current': isCurrentCheckin(checkin) }]"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <span class="font-weight-medium">{{ checkin.event_name || '-' }}</span>
                    <CBadge color="primary" class="badge-outline ml-2" v-if="checkin.picacode">
                      {{ checkin.picacode }}
                    </CBadge>
                    <CBadge v-if="checkin.photos_count" color="info" class="ml-1">
                      {{ checkin.photos_count }} foto
                    </CBadge>
                  </div>
                  <div class="text-right text-nowrap small text-muted">
                    <template v-if="checkin.start_of_stay || checkin.end_of_stay">
                      {{ checkin.start_of_stay|formatDateTime('L') }} - {{ checkin.end_of_stay|formatDateTime('L') }}
                    </template>
                    <template v-else>
                      {{ checkin.checkin_date|formatDateTime('L LT') }}
                    </template>
                  </div>
                </div>
              </router-link>

              <CButton
                v-if="hasMoreCheckins"
                color="link"
                size="sm"
                class="p-0 mt-2"
                :disabled="loadingMoreCheckins"
                @click="loadMoreCheckins"
              >
                <CSpinner v-if="loadingMoreCheckins" size="sm" class="mr-1"/>
                <translate translate-context="accounting.user">Show more</translate>
              </CButton>
            </div>
          </CCol>
        </CRow>
      </div>
      <div v-else class="text-muted">
        <translate translate-context="accounting.user">No user information</translate>
      </div>
    </CCardBody>
  </CCard>

</template>

<script>
import {mapActions} from 'vuex'
import {getDetailRouteParams, TRANSACTION_TYPE_LABELS} from '../constants'
import {currencyCodes} from "../../../constants";

const TRANSACTION_BADGE_COLORS = {
  order: 'success',
  order_item: 'success',
  refund: 'danger',
  service_order: 'info',
  payout: 'primary'
}

export default {
  name: 'UserInfo',
  props: {
    user: {
      type: Object,
      default: null
    },
    userId: {
      type: [Number, String],
      default: null
    },
    currentSourceId: {
      type: [Number, String],
      default: null
    },
    currentCheckinPicacodes: {
      type: Array,
      default: () => []
    },
    scope: {
      type: Object,
      default: () => ({type: 'global', id: null})
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loadingTransactions: false,
      loadingMoreTransactions: false,
      transactions: [],
      totalTransactions: 0,
      loadingCheckins: false,
      loadingMoreCheckins: false,
      checkins: [],
      totalCheckins: 0
    }
  },
  computed: {
    currencyCodes() {
      return currencyCodes
    },
    userDisplayName() {
      if (!this.user) return `User ${this.userId}`
      const email = this.user.email
      const name = this.user.full_name
      if (email && name) return `${email} - ${name}`
      return email || name || `User ${this.userId}`
    },
    hasMoreTransactions() {
      return this.totalTransactions > this.transactions.length
    },
    hasMoreCheckins() {
      return this.totalCheckins > this.checkins.length
    }
  },
  mounted() {
    if (this.userId) {
      this.loadData()
    }
  },
  watch: {
    userId(newVal) {
      if (newVal) {
        this.loadData()
      }
    }
  },
  methods: {
    ...mapActions('accounting', ['fetchUserTransactions', 'fetchUserCheckins']),

    async loadData() {
      this.loadTransactions()
      this.loadCheckins()
    },

    async loadTransactions(limit = 5, append = false) {
      if (!this.userId) return

      if (append) {
        this.loadingMoreTransactions = true
      } else {
        this.loadingTransactions = true
      }

      try {
        const result = await this.fetchUserTransactions({
          userId: this.userId,
          limit
        })
        const newTransactions = result.results || result || []
        if (append) {
          this.transactions = [...this.transactions, ...newTransactions]
        } else {
          this.transactions = newTransactions
        }
        this.totalTransactions = result.total || this.transactions.length
      } catch (error) {
        if (!append) {
          this.transactions = []
          this.totalTransactions = 0
        }
      } finally {
        this.loadingTransactions = false
        this.loadingMoreTransactions = false
      }
    },

    async loadMoreTransactions() {
      await this.loadTransactions(100, true)
    },

    async loadCheckins(limit = 5, append = false) {
      if (!this.userId) return

      if (append) {
        this.loadingMoreCheckins = true
      } else {
        this.loadingCheckins = true
      }

      try {
        const result = await this.fetchUserCheckins({
          userId: this.userId,
          limit
        })
        const newCheckins = result.results || result || []
        if (append) {
          this.checkins = [...this.checkins, ...newCheckins]
        } else {
          this.checkins = newCheckins
        }
        this.totalCheckins = result.total || this.checkins.length
      } catch (error) {
        if (!append) {
          this.checkins = []
          this.totalCheckins = 0
        }
      } finally {
        this.loadingCheckins = false
        this.loadingMoreCheckins = false
      }
    },

    async loadMoreCheckins() {
      await this.loadCheckins(100, true)
    },

    getTransactionRoute(tx) {
      return getDetailRouteParams(tx.transaction_type, tx.source_id, this.scope)
    },

    isCurrentTransaction(tx) {
      if (!this.currentSourceId) return false
      return String(tx.source_id) === String(this.currentSourceId)
    },

    isCurrentCheckin(checkin) {
      if (!this.currentCheckinPicacodes || this.currentCheckinPicacodes.length === 0) return false
      return this.currentCheckinPicacodes.includes(checkin.picacode)
    },

    getTransactionLabel(tx) {
      const typeLabel = TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type
      return `${typeLabel} #${tx.source_id}`
    },

    getTransactionTypeLabel(type) {
      return TRANSACTION_TYPE_LABELS[type] || type
    },

    getTransactionBadgeColor(type) {
      return TRANSACTION_BADGE_COLORS[type] || 'secondary'
    },

    getCheckinRoute(checkin) {
      if (checkin.pica_service_id) {
        return {
          name: 'checkin.detail',
          params: {
            picaServiceId: checkin.pica_service_id,
            id: checkin.id
          }
        }
      }
      return {}
    },

    getAmountClass(amount) {
      const num = parseFloat(amount)
      if (num > 0) return 'text-success'
      if (num < 0) return 'text-danger'
      return ''
    },
  }
}
</script>

<style scoped>
.user-items-list {
  max-height: 500px;
  overflow-y: auto;
}

.list-item {
  display: block;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #ebedef;
  border-radius: 4px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background-color: #f8f9fa;
  text-decoration: none;
  color: inherit;
}

.list-item-current {
  background-color: #e8f4fd;
  border-left: 3px solid #321fdb;
}
</style>
